/**
 * PatientLookupContext — global provider so any list / table / profile page
 * can open the shared patient dialogs with a single line:
 *
 *   const { openLookup, openEdit, openScheduler } = usePatientLookup();
 *   openLookup(contactId, displayName)   // compact quick-view slider
 *   openEdit(contactId, displayName)     // FULL editable side panel
 *   openScheduler({ id, name, email, phone })  // scheduling grid popup pre-selected
 *
 * The provider mounts:
 *   1. PatientLookupSlider (compact, read-only overview)
 *   2. PatientDetailPanel   (rich editable tabs)
 *   3. SchedulingGridDialog (the master multi-PT booking grid — patient
 *      pre-selected so any "Book Appointment" button lands the FD user
 *      directly in the availability view instead of a bare form).
 *
 * Location is pulled from AuthContext.currentLocation — the same instance
 * scales to workspaces / 20+ clinics because switching location automatically
 * re-scopes every dialog.
 */

import React, { createContext, useCallback, useContext, useState } from 'react';
import { useAuth } from './AuthContext';
import { PatientLookupSlider } from '../components/patients/PatientLookupSlider';
import { GlobalPatientSearch } from '../components/patients/GlobalPatientSearch';
import { PatientDetailPanel } from '../components/call-tracker/PatientDetailPanel';
import { SchedulingGridDialog } from '../components/call-tracker/SchedulingGridDialog';

const PatientLookupContext = createContext(null);

export function PatientLookupProvider({ children }) {
  const { currentLocation } = useAuth();
  const [lookupState, setLookupState] = useState({ open: false, contactId: null, name: '' });
  const [editState, setEditState] = useState({ open: false, contactInfo: null });
  const [schedulerState, setSchedulerState] = useState({ open: false, initialContact: null });

  const openLookup = useCallback((contactId, displayName = '') => {
    if (!contactId) return;
    setLookupState({ open: true, contactId, name: displayName });
  }, []);

  const closeLookup = useCallback(() => {
    setLookupState((s) => ({ ...s, open: false }));
  }, []);

  const openEdit = useCallback((contactId, displayName = '', extra = {}) => {
    if (!contactId) return;
    setLookupState((s) => ({ ...s, open: false }));
    setEditState({
      open: true,
      contactInfo: {
        contactId,
        contactName: displayName,
        phone: extra.phone || '',
        email: extra.email || '',
        ...extra,
      },
    });
  }, []);

  const closeEdit = useCallback(() => {
    setEditState((s) => ({ ...s, open: false }));
  }, []);

  /**
   * Open the SchedulingGridDialog with a patient pre-selected. Accepts either
   * a patient object ({ id | contactId, name, email, phone, firstName, lastName })
   * or a bare contactId string.
   */
  const openScheduler = useCallback((patient) => {
    if (!patient) return;
    const p = typeof patient === 'string' ? { contactId: patient } : patient;
    const contactId = p.contactId || p.id || '';
    if (!contactId) return;
    // Normalise the shape SchedulingGridDialog + AddAppointmentDialog expect.
    const [firstName = '', ...rest] = (p.name || '').trim().split(/\s+/);
    setSchedulerState({
      open: true,
      initialContact: {
        id: contactId,
        contactId,
        firstName: p.firstName || firstName,
        lastName: p.lastName || rest.join(' '),
        name: p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim(),
        email: p.email || '',
        phone: p.phone || '',
      },
    });
  }, []);

  const closeScheduler = useCallback(() => {
    setSchedulerState((s) => ({ ...s, open: false }));
  }, []);

  const locationId = currentLocation?.id;

  return (
    <PatientLookupContext.Provider
      value={{ openLookup, closeLookup, openEdit, closeEdit, openScheduler, closeScheduler }}
    >
      {children}
      {locationId && (
        <>
          <PatientLookupSlider
            open={lookupState.open}
            onOpenChange={(v) => setLookupState((s) => ({ ...s, open: v }))}
            contactId={lookupState.contactId}
            locationId={locationId}
            initialName={lookupState.name}
            onEdit={(cid, name, extra) => openEdit(cid, name, extra)}
          />
          <PatientDetailPanel
            open={editState.open}
            onOpenChange={(v) => setEditState((s) => ({ ...s, open: v }))}
            call={editState.contactInfo}
            locationId={locationId}
          />
          <SchedulingGridDialog
            open={schedulerState.open}
            onOpenChange={(v) => setSchedulerState((s) => ({ ...s, open: v }))}
            initialContact={schedulerState.initialContact}
            locationId={locationId}
          />
          <GlobalPatientSearch />
        </>
      )}
    </PatientLookupContext.Provider>
  );
}

export function usePatientLookup() {
  const ctx = useContext(PatientLookupContext);
  if (!ctx) {
    return {
      openLookup: () => {}, closeLookup: () => {},
      openEdit: () => {}, closeEdit: () => {},
      openScheduler: () => {}, closeScheduler: () => {},
    };
  }
  return ctx;
}
